import React, { useState, useRef } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { UploadCloud, Link2, Type, FileText, Loader2, Plus } from "lucide-react";
import { docsApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export const UploadDialog = ({ onUploaded, trigger, workspaceId }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [url, setUrl] = useState("");
    const [title, setTitle] = useState("");
    const [text, setText] = useState("");
    const fileRef = useRef(null);

    const done = (doc, msg) => {
        toast.success(msg);
        onUploaded?.(doc);
        setOpen(false);
        setUrl(""); setTitle(""); setText("");
    };

    const handleFiles = async (files) => {
        const file = files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const doc = await docsApi.uploadFile(file, undefined, workspaceId);
            done(doc, t("upload.processing", { title: doc.title }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("upload.uploadFailed"));
        } finally {
            setBusy(false);
        }
    };

    const submitUrl = async () => {
        if (!url.trim()) return;
        setBusy(true);
        try {
            const doc = await docsApi.uploadUrl(url.trim(), "auto", workspaceId);
            done(doc, t("upload.imported", { title: doc.title }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("upload.urlFailed"));
        } finally {
            setBusy(false);
        }
    };

    const submitText = async () => {
        if (text.trim().split(/\s+/).length < 5) { toast.error(t("upload.pasteMore")); return; }
        setBusy(true);
        try {
            const doc = await docsApi.uploadText(title.trim() || null, text.trim(), "auto", workspaceId);
            done(doc, t("upload.added", { title: doc.title }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("upload.textFailed"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button data-testid="library-upload-button" className="gap-2"><Plus className="h-4 w-4" /> {t("upload.addContent")}</Button>
                )}
            </DialogTrigger>
            <DialogContent className="border-border bg-surface-1 sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-sans-display text-t1">{t("upload.title")}</DialogTitle>
                </DialogHeader>
                <Tabs defaultValue="file" className="mt-2">
                    <TabsList className="grid w-full grid-cols-3 bg-surface-2">
                        <TabsTrigger value="file" className="gap-1.5 text-xs"><UploadCloud className="h-3.5 w-3.5" /> {t("upload.tabFile")}</TabsTrigger>
                        <TabsTrigger value="url" className="gap-1.5 text-xs"><Link2 className="h-3.5 w-3.5" /> {t("upload.tabUrl")}</TabsTrigger>
                        <TabsTrigger value="text" className="gap-1.5 text-xs"><Type className="h-3.5 w-3.5" /> {t("upload.tabText")}</TabsTrigger>
                    </TabsList>

                    <TabsContent value="file" className="mt-4">
                        <div
                            data-testid="library-upload-dropzone"
                            onClick={() => fileRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
                            className={cn(
                                "flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed p-8 text-center transition-colors",
                                dragOver ? "border-[hsl(var(--primary))] bg-surface-2" : "border-border bg-surface-2/50 hover:bg-surface-2"
                            )}
                        >
                            {busy ? <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--primary))]" /> : <UploadCloud className="h-8 w-8 text-[hsl(var(--primary))]" />}
                            <p className="mt-3 text-sm font-medium text-t1">{t("upload.dropTitle")}</p>
                            <p className="mt-1 text-xs text-t4">{t("upload.dropHint")}</p>
                            <input ref={fileRef} type="file" accept=".pdf,.epub,.docx,.txt,.md" className="hidden" data-testid="library-file-input" onChange={(e) => handleFiles(e.target.files)} />
                        </div>
                    </TabsContent>

                    <TabsContent value="url" className="mt-4 space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-t2">{t("upload.urlLabel")}</Label>
                            <Input data-testid="library-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("upload.urlPlaceholder")} className="bg-surface-2" />
                        </div>
                        <Button data-testid="library-url-submit" className="w-full gap-2" disabled={busy} onClick={submitUrl}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} {t("upload.importArticle")}
                        </Button>
                    </TabsContent>

                    <TabsContent value="text" className="mt-4 space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-t2">{t("upload.titleLabel")}</Label>
                            <Input data-testid="library-text-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("upload.titlePlaceholder")} className="bg-surface-2" />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-t2">{t("upload.pasteLabel")}</Label>
                            <Textarea data-testid="library-text-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("upload.pastePlaceholder")} className="min-h-[140px] bg-surface-2" />
                        </div>
                        <Button data-testid="library-text-submit" className="w-full gap-2" disabled={busy} onClick={submitText}>
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} {t("upload.addText")}
                        </Button>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};
